import type {
    IAbsorbElement,
    IAbsorbServiceProps,
} from '@src/slide/application/service/absorb-manager/absorb-manager.interface';
import { MODES } from '@src/slide/domain/ui-model/element-modeles/property/frame/frame.interface';
import { IOperationFrame, IOriginalSize } from '@src/slide/application/service/guides-manager/index.interface';
import {
    IElementBaseModel,
} from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { IGroupModel } from '@src/slide/domain/ui-model/element-modeles/Interface/group-model.interface';

/**
 * 吸附服务
 */
export default class AbsorbLogic {
    /**
     * 吸附x轴坐标
     */
    private absorbPointX: Array<number> = [];
    // 吸附y轴坐标
    private absorbPointY: Array<number> = [];
    // 吸附步长
    private absorbStep: number;
    // 是否需要尾吸附
    private isAbsorbEndPoint = false;

    constructor(props: IAbsorbServiceProps) {
        const { isAbsorbEndPoint } = props;
        this.isAbsorbEndPoint = isAbsorbEndPoint;
    }

    /**
     * 更新吸附点
     * @param absorbPointX
     * @param absorbPointY
     */
    public updateAbsorbPoint(absorbPointX: Array<number>, absorbPointY: Array<number>) {
        this.absorbPointX = absorbPointX;
        this.absorbPointY = absorbPointY;
    }

    /**
     * 更新吸附步长
     * @param absorbStep
     */
    public updateAbsorbStep(absorbStep: number) {
        this.absorbStep = absorbStep;
    }

    /**
     * 响应吸附
     * @param props
     */
    public handleAbsorbElement(props: IAbsorbElement) {
        const {
            offsetFrame,
            originalSize,
            mode,
            model,
            multiRect,
            sizeMap,
        } = props;
        const dragOriginSize = multiRect?.length ? multiRect : originalSize;
        const resizeOffsetFrame = sizeMap?.size ? sizeMap.get(model.key)?.offsetFrame : offsetFrame;
        switch (mode) {
            case MODES.MOVE:
                this.handleAbsorbDraggingElement({
                    originalSize: dragOriginSize,
                    offsetFrame,
                });
                break;
            default:
                this.handleAbsorbResizingElement({
                    originalSize,
                    offsetFrame: resizeOffsetFrame,
                    model,
                });
        }
    }

    /**
     * 处理拖拽吸附
     */
    private handleAbsorbDraggingElement(props: {
        originalSize: IOriginalSize,
        offsetFrame: IOperationFrame,
    }) {
        this.handleAbsorbDraggingXPosition(props);
        this.handleAbsorbDraggingYPosition(props);
    }

    /**
     * 处理x轴拖拽吸附
     */
    private handleAbsorbDraggingXPosition(props: {
        originalSize: IOriginalSize,
        offsetFrame: IOperationFrame,
    }) {
        const { originalSize, offsetFrame } = props;
        const { left: originLeft, width: originWidth } = originalSize;
        const { left: offsetLeft } = offsetFrame;
        const newOffsetLeft = this.computeAbsorbStartPoint({
            originStart: originLeft,
            offsetStart: offsetLeft,
            originalLength: originWidth,
            absorbPoint: this.absorbPointX,
        });

        offsetFrame.left = newOffsetLeft;
    }

    /**
     * 处理y轴拖拽吸附
     */
    private handleAbsorbDraggingYPosition(props: {
        originalSize: IOriginalSize,
        offsetFrame: IOperationFrame,
    }) {
        const { originalSize, offsetFrame } = props;
        const { top: originTop, height: originHeight } = originalSize;
        const { top: offsetTop } = offsetFrame;
        const newOffsetTop = this.computeAbsorbStartPoint({
            originStart: originTop,
            offsetStart: offsetTop,
            originalLength: originHeight,
            absorbPoint: this.absorbPointY,
        });

        offsetFrame.top = newOffsetTop;
    }

    /**
     * 计算吸附后的坐标
     * @private
     */
    private computeAbsorbStartPoint(props: {
        originStart: number,
        offsetStart: number,
        originalLength: number,
        absorbPoint: Array<number>,
    }): number {
        const { originStart, offsetStart, originalLength, absorbPoint } = props;
        let absorbedStart = offsetStart;

        // 最小吸附，多个吸附点同时满足吸附时，取吸附差值最小的那个点
        let minAbsorb = Number.MAX_VALUE;
        absorbPoint.forEach((point) => {
            // 计算起始点吸附导致的坐标修改
            const startPointDiff = Math.abs(originStart + offsetStart - point);
            if (startPointDiff < this.absorbStep && startPointDiff <= minAbsorb) {
                absorbedStart = point - originStart;
                minAbsorb = startPointDiff;
            }

            // 计算结尾点吸附导致的坐标修改
            if (this.isAbsorbEndPoint) {
                const EndPointDiff = Math.abs(originStart + offsetStart + originalLength - point);
                if (EndPointDiff < this.absorbStep && EndPointDiff <= minAbsorb) {
                    absorbedStart = point - (originStart + originalLength);
                    minAbsorb = EndPointDiff;
                }
            }
        });

        return absorbedStart;
    }

    /**
     * 处理缩放吸附
     * @private
     */
    private handleAbsorbResizingElement(props: {
        originalSize: IOriginalSize,
        offsetFrame: IOperationFrame,
        model: IElementBaseModel,
    }) {
        this.handleAbsorbResizingXPosition(props);
        this.handleAbsorbResizingYPosition(props);
    }

    private handleAbsorbResizingXPosition(props: {
        originalSize: IOriginalSize,
        offsetFrame: IOperationFrame,
        model: IElementBaseModel,
    }) {
        const { offsetFrame, originalSize, model } = props;

        // 处理组合
        let parentLeft = 0;
        if (model && model.parent?.type === UIModelType.GROUP) {
            parentLeft = (model.parent as IGroupModel).props.style.left || 0;
        }

        // 判断是否是反转场景
        const flipH = model?.props?.style?.flipH;
        const isRevert = ((!!offsetFrame.flipH !== !!flipH));
        const originStart = originalSize.left + parentLeft;

        const offsetStart = offsetFrame.left;
        const offsetLength = offsetFrame.width;
        const originalLength = originalSize.width;


        const { newOffsetStart, newOffsetLength } = this.computeAbsorbResizingPosition({
            isRevert,
            originStart,
            originalLength,
            offsetStart,
            offsetLength,
            absorbPoint: this.absorbPointX,
        });

        offsetFrame.left = newOffsetStart;
        offsetFrame.width = newOffsetLength;
    }

    private handleAbsorbResizingYPosition(props: {
        originalSize: IOriginalSize,
        offsetFrame: IOperationFrame,
        model: IElementBaseModel,
    }) {
        const { offsetFrame, originalSize, model } = props;

        // 处理组合
        let parentTop = 0;
        if (model && model.parent?.type === UIModelType.GROUP) {
            parentTop = (model.parent as IGroupModel).props.style.top || 0;
        }

        // 判断是否是反转场景
        const flipV = model?.props?.style?.flipV;
        const isRevert = ((!!offsetFrame.flipV !== !!flipV));
        const originStart = originalSize.top + parentTop;
        const offsetStart = offsetFrame.top;
        const offsetLength = offsetFrame.height;
        const originalLength = originalSize.height;

        const { newOffsetStart, newOffsetLength } = this.computeAbsorbResizingPosition({
            isRevert,
            originStart,
            originalLength,
            offsetStart,
            offsetLength,
            absorbPoint: this.absorbPointY,
        });

        offsetFrame.top = newOffsetStart;
        offsetFrame.height = newOffsetLength;
    }

    private computeAbsorbResizingPosition(props: {
        originStart: number;
        originalLength: number;
        offsetStart: number;
        offsetLength: number;
        isRevert: boolean;
        absorbPoint: Array<number>
    }) {
        const { originStart, originalLength, isRevert, absorbPoint } = props;
        let { offsetStart, offsetLength } = props;
        let minAbsorb = Number.MAX_VALUE;

        // eslint-disable-next-line complexity
        absorbPoint.forEach((position) => {
            if (isRevert) {
                const topDiff = Math.abs(originStart + originalLength + offsetStart + offsetLength - position);
                // 吸附右/下边框
                if (offsetStart > 0) {
                    if (topDiff < this.absorbStep / 2 && topDiff <= minAbsorb) {
                        offsetLength = position - originStart - originalLength - originalLength;
                        minAbsorb = topDiff;
                    }
                } else {
                    // 吸附左/上边框
                    const topDiff = Math.abs(originStart + offsetStart - position);
                    if (topDiff < this.absorbStep / 2 && topDiff <= minAbsorb) {
                        const diff = position - (originStart + offsetStart);
                        offsetStart = position - originStart;
                        offsetLength = offsetLength - diff;
                        minAbsorb = topDiff;
                    }
                }
            } else {
                // 吸附右/下边框
                const topDiff = Math.abs(originStart + originalLength + offsetLength - position);
                if (Math.floor(Math.abs(offsetStart)) === 0) {
                    if (topDiff < this.absorbStep / 2 && topDiff < minAbsorb) {
                        offsetLength = position - originStart - originalLength;
                        minAbsorb = topDiff;
                    }
                } else {
                    // 吸附左/上边框
                    const topDiff = Math.abs(originStart + offsetStart - position);
                    if (topDiff < this.absorbStep / 2 && topDiff < minAbsorb) {
                        offsetStart = position - originStart;
                        offsetLength = -offsetStart;
                        minAbsorb = topDiff;
                    }
                }
            }
        });

        return {
            newOffsetStart: offsetStart,
            newOffsetLength: offsetLength,
        };
    }
}
